import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, ListItemIcon, ListItemText,
  Menu, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import AddLinkIcon from '@mui/icons-material/AddLink';
import { getV2App, listV2Apps, updateV2App } from '../../../api/studioV2.api';
import {
  chatFlowOf, panelLabel, setChatFlow, setPanelFlow,
} from '../appBuilder/appBuilderModel';
import { flowUsage } from './flowBuilderModel';

const CHAT_TARGET = '__chat__';

/** Places in an app a flow can drive: the chat, and each data panel. */
export function appFlowTargets(def, flowNames = {}) {
  const panels = Array.isArray(def?.panels) ? def.panels : [];
  const currentName = (ref) => (ref ? (flowNames[ref] || 'another flow') : 'nothing yet');
  const targets = [];
  if (panels.some((p) => p?.type === 'chat')) {
    targets.push({ id: CHAT_TARGET, label: 'The chat', current: currentName(chatFlowOf(def)) });
  }
  panels.filter((p) => p?.type === 'component').forEach((p) => {
    targets.push({
      id: p.id, label: `Panel “${p.title || panelLabel(p)}”`, current: currentName(p.source?.flowRef),
    });
  });
  return targets;
}

export function applyFlowToApp(def, targetId, flowMongoId) {
  return targetId === CHAT_TARGET ? setChatFlow(def, flowMongoId) : setPanelFlow(def, targetId, flowMongoId);
}

/** Header control: which apps use this flow, with a shortcut to connect it to another app. */
export default function FlowAppsButton({
  flowMongoId, flowName, readOnly, onNotify,
}) {
  const [apps, setApps] = useState([]);
  const [anchor, setAnchor] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [appId, setAppId] = useState('');
  const [targetId, setTargetId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const { apps: rows } = await listV2Apps();
    setApps(Array.isArray(rows) ? rows : []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const usedBy = useMemo(() => flowUsage(apps).get(String(flowMongoId)) || [], [apps, flowMongoId]);
  const chosenApp = apps.find((a) => String(a._id) === appId);
  const targets = useMemo(
    () => appFlowTargets(chosenApp?.definition, { [String(flowMongoId)]: flowName || 'this flow' }),
    [chosenApp, flowMongoId, flowName],
  );

  const openDialog = () => {
    setAnchor(null);
    setError(null);
    const first = apps[0];
    setAppId(first ? String(first._id) : '');
    setTargetId('');
    setDialogOpen(true);
  };

  useEffect(() => {
    if (!targets.some((t) => t.id === targetId)) setTargetId(targets[0]?.id || '');
  }, [targets, targetId]);

  const onConnect = async () => {
    if (!appId || !targetId) return;
    setSaving(true);
    setError(null);
    const { app, error: loadErr } = await getV2App(appId);
    if (loadErr || !app?.definition) {
      setSaving(false);
      setError(loadErr || 'The app could not be loaded.');
      return;
    }
    const { error: saveErr } = await updateV2App(appId, applyFlowToApp(app.definition, targetId, String(flowMongoId)));
    setSaving(false);
    if (saveErr) {
      setError(saveErr);
      return;
    }
    setDialogOpen(false);
    onNotify?.(`Connected to ${app.name || app.definition.name || 'the app'}`);
    load();
  };

  const label = usedBy.length
    ? `Used in ${usedBy.length} ${usedBy.length === 1 ? 'app' : 'apps'}`
    : 'Not in an app yet';

  return (
    <>
      <Button
        size="small"
        variant="text"
        color="inherit"
        startIcon={<ViewModuleOutlinedIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        data-testid="flow-apps"
        sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 600 }}
      >
        {label}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {usedBy.map((a) => (
          <MenuItem key={a.id} component={RouterLink} to={`/studio/apps/${a.id}`} data-testid="flow-app-link">
            <ListItemText primary={a.name} secondary={a.roles.join(', ')} />
          </MenuItem>
        ))}
        {usedBy.length > 0 && <Divider />}
        <MenuItem onClick={openDialog} disabled={readOnly || !apps.length} data-testid="flow-use-in-app">
          <ListItemIcon><AddLinkIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Use in an app…" secondary={apps.length ? null : 'Create an app first'} />
        </MenuItem>
      </Menu>
      <Dialog open={dialogOpen} onClose={saving ? undefined : () => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Use this flow in an app</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField select size="small" label="App" value={appId} onChange={(e) => setAppId(e.target.value)} fullWidth inputProps={{ 'data-testid': 'flow-use-app' }}>
              {apps.map((a) => (
                <MenuItem key={a._id} value={String(a._id)}>{a.name || a.definition?.name || 'Untitled app'}</MenuItem>
              ))}
            </TextField>
            {targets.length ? (
              <TextField select size="small" label="Where in the app" value={targetId} onChange={(e) => setTargetId(e.target.value)} fullWidth inputProps={{ 'data-testid': 'flow-use-target' }}>
                {targets.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    <ListItemText primary={t.label} secondary={`Now: ${t.current}`} />
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Typography variant="body2" color="text.secondary">
                This app has no chat or data panels yet. Add one in the app builder first.
              </Typography>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={onConnect} disabled={saving || !appId || !targetId} data-testid="flow-use-connect">
            {saving ? 'Connecting…' : 'Connect'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
